import React, {FC} from 'react';
import styled from 'styled-components';

import CSVImgSrc from '../../../../../../../src/assets/images/attachmentExcel16.svg';
import HubspotImgSrc from '../../../../../../../src/assets/images/integrations/hubspot16.svg';
import MicrosoftImgSrc from '../../../../../../../src/assets/images/integrations/microsoft16.svg';
import OutlookImgSrc from '../../../../../../../src/assets/images/integrations/outlook16.svg';
import SalesforceImgSrc from '../../../../../../../src/assets/images/integrations/salesforce16.svg';
import {DropdownCategory} from '../../../../../../../src/components/common/dropdowns/dropdownCategory';
import {DropdownItemIconImage} from '../../../../../../../src/components/common/dropdowns/dropdownItemIconImage';
import {Pill} from '../../../../../../../src/components/pill/pill';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../../shared/ui/src/components/dropdowns/dropdownList';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../shared/ui/src/styles/commonStyles';
import {ImportCSVButton} from './buttons/importCSVButton';

/*
 * Constants.
 */

const dropdownWidth = 187; // px.
const dropdownWidthWithPill = 220; // px.
const dropdownWidthWithOutlookDirectory = 226; // px.

const StyledPillWrapperDiv = styled.div`
  grid-area: badges;
`;

/*
 * Props.
 */

interface ContactsManagerImportPopoverProps {
  onImportCSVClick: Handler;
  onImportSalesforceClick?: Handler;
  onImportHubspotClick?: Handler;
  onImportMicrosoftDynamicsClick?: Handler;
  onImportOutloookDirectoryClick?: Handler;
  hasCrmSyncFeature: boolean;
  isDisabled?: boolean;
  buttonVariant?: ButtonVariantsEnum;
}

/*
 * Component.
 */

export const ContactsManagerImportPopover: FC<ContactsManagerImportPopoverProps> = (props) => {
  const {
    isDisabled,
    onImportSalesforceClick,
    onImportHubspotClick,
    onImportCSVClick,
    onImportMicrosoftDynamicsClick,
    onImportOutloookDirectoryClick,
  } = props;

  if (
    !onImportSalesforceClick &&
    !onImportHubspotClick &&
    !onImportMicrosoftDynamicsClick &&
    !onImportOutloookDirectoryClick
  ) {
    return <ImportCSVButton onClick={onImportCSVClick} isDisabled={isDisabled} />;
  }
  return (
    <PopoverCoordinator
      isDisabled={isDisabled}
      renderButton={(hasHover) => (
        <ImportCSVButton isDisabled={isDisabled} variant={props.buttonVariant} hasHover={hasHover} />
      )}
      renderModal={(popoverProps) => renderDropdown(popoverProps, props)}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdown(popoverProps: PopoverTransferProps, props: ContactsManagerImportPopoverProps) {
  const {
    onImportCSVClick,
    onImportSalesforceClick,
    onImportHubspotClick,
    onImportMicrosoftDynamicsClick,
    onImportOutloookDirectoryClick,
    hasCrmSyncFeature,
  } = props;
  return (
    <Dropdown {...popoverProps} placement="bottom-end" width={getDropdownWidth(props)}>
      <DropdownList>
        {onImportOutloookDirectoryClick && (
          <DropdownCategory>
            <Trans id="contacts-manager-import-popover-any-contacts-category">Any contacts</Trans>
          </DropdownCategory>
        )}
        {renderCSVDropdownItem(popoverProps, onImportCSVClick)}
        {onImportSalesforceClick &&
          renderSalesforceDropdownItem(popoverProps, onImportSalesforceClick, hasCrmSyncFeature)}
        {onImportHubspotClick &&
          renderHubspotDropdownItem(popoverProps, onImportHubspotClick, hasCrmSyncFeature)}
        {onImportMicrosoftDynamicsClick &&
          renderMicrosoftDynamicsDropdownItem(
            popoverProps,
            onImportMicrosoftDynamicsClick,
            hasCrmSyncFeature,
          )}
        {onImportOutloookDirectoryClick && (
          <>
            <DropdownCategory>
              <Trans id="contacts-manager-import-popover-internal-contacts-category">Internal contacts</Trans>
            </DropdownCategory>
            {renderOutlookDirectoryDropdownItem(popoverProps, onImportOutloookDirectoryClick)}
          </>
        )}
      </DropdownList>
    </Dropdown>
  );
}

function renderCSVDropdownItem(popoverProps: PopoverTransferProps, onImportCSVClick: Handler) {
  const onSelect = () => {
    popoverProps.onRequestClose();
    onImportCSVClick();
  };
  return (
    <DropdownItem onSelect={onSelect}>
      <DropdownItemIconImage size={16} source={CSVImgSrc} />
      <DropdownItemContent>
        <Trans id="contacts-manager-import-accounts-dropdown-csv">from CSV</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

function maybeRenderGrowthPill(hasCrmSyncFeature: boolean) {
  if (hasCrmSyncFeature) {
    return undefined;
  }

  return (
    <StyledPillWrapperDiv>
      <Pill size={VisualSizesEnum.MEDIUM}>
        <Trans id="crm-sync-growth-pill">GROWTH</Trans>
      </Pill>
    </StyledPillWrapperDiv>
  );
}

function renderSalesforceDropdownItem(
  popoverProps: PopoverTransferProps,
  onImportSalesforceClick: Handler,
  hasCrmSyncFeature: boolean,
) {
  const onSelect = () => {
    popoverProps.onRequestClose();
    onImportSalesforceClick();
  };
  return (
    <DropdownItem onSelect={onSelect}>
      <DropdownItemIconImage size={16} source={SalesforceImgSrc} />
      <DropdownItemContent>
        <Trans id="contacts-manager-import-accounts-dropdown-salesforce">from Salesforce</Trans>
      </DropdownItemContent>
      {maybeRenderGrowthPill(hasCrmSyncFeature)}
    </DropdownItem>
  );
}

function renderHubspotDropdownItem(
  popoverProps: PopoverTransferProps,
  onImportHubspotClick: Handler,
  hasCrmSyncFeature: boolean,
) {
  const onSelect = () => {
    popoverProps.onRequestClose();
    onImportHubspotClick();
  };
  return (
    <DropdownItem onSelect={onSelect}>
      <DropdownItemIconImage size={16} source={HubspotImgSrc} />
      <DropdownItemContent>
        <Trans id="contacts-manager-import-accounts-dropdown-hubspot">from Hubspot</Trans>
      </DropdownItemContent>
      {maybeRenderGrowthPill(hasCrmSyncFeature)}
    </DropdownItem>
  );
}

function renderMicrosoftDynamicsDropdownItem(
  popoverProps: PopoverTransferProps,
  onImportMicrosoftDynamicsClick: Handler,
  hasCrmSyncFeature: boolean,
) {
  const onSelect = () => {
    popoverProps.onRequestClose();
    onImportMicrosoftDynamicsClick();
  };
  return (
    <DropdownItem onSelect={onSelect}>
      <DropdownItemIconImage size={16} source={MicrosoftImgSrc} />
      <DropdownItemContent>
        <Trans id="contacts-manager-import-accounts-dropdown-microsoft-dynamics">from Dynamics 365</Trans>
      </DropdownItemContent>
      {maybeRenderGrowthPill(hasCrmSyncFeature)}
    </DropdownItem>
  );
}

function renderOutlookDirectoryDropdownItem(
  popoverProps: PopoverTransferProps,
  onImportOutloookDirectoryClick: Handler,
) {
  const onSelect = () => {
    popoverProps.onRequestClose();
    onImportOutloookDirectoryClick();
  };
  return (
    <DropdownItem onSelect={onSelect}>
      <DropdownItemIconImage size={16} source={OutlookImgSrc} />
      <DropdownItemContent>
        <Trans id="contacts-manager-import-contacts-dropdown-outlook-directory">from Outlook Directory</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

function getDropdownWidth(props: ContactsManagerImportPopoverProps) {
  const {hasCrmSyncFeature, onImportOutloookDirectoryClick} = props;
  if (onImportOutloookDirectoryClick) {
    return dropdownWidthWithOutlookDirectory;
  }
  if (!hasCrmSyncFeature) {
    return dropdownWidthWithPill;
  }
  return dropdownWidth;
}
